import { useSelector } from "react-redux";
import {
  selectSubGroupConfiguration,
  selectSubGroupUsers,
} from "app/slices/admin.slice";
import useSearchInput from "utils/hooks/useSearchInput";
import { filterObjectsBySearch } from "utils/general";
import UsersTable from "features/admin/group/users/UsersTable";
import { FixMeLater } from "types";
import React, { useState } from "react";
import LicensesModal from "features/admin/group/users/LicensesModal";
import useFilters from "features/admin/group/users/useFilters";
import { AssignLicensesFailureType, LanguageNameSpaces } from "@shared/types";
import Flex from "common/Boxes/Flex";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import styled, { useTheme } from "styled-components";
import TablePanel from "features/admin/NewLook/TablePanel";
import PanelTable from "features/admin/NewLook/PanelTable";
import FilterPills from "features/admin/NewLook/FilterPills";

const SEARCH_FIELDS = [
  ["telegram_user", "first_name"],
  ["telegram_user", "last_name"],
  ["telegram_user", "username"],
  ["telegram_user", "telegram_user_id"],
];

interface UsersProps {
  subGroupID: number;
  isFetching: boolean;
  mobileActions?: React.JSX.Element;
}

const Users = ({ subGroupID, isFetching, mobileActions }: UsersProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();
  const isNewLookDesktop = isNewLook && !isBottomNavigator;
  const subGroupUsers = useSelector(selectSubGroupUsers(subGroupID));
  const subGroupConfiguration = useSelector(
    selectSubGroupConfiguration(subGroupID)
  );
  const inputParams: FixMeLater = {
    placeholder: t("users.searchPlaceholder"),
    style: isNewLookDesktop
      ? { width: 240, height: 32 }
      : isNewLook
      ? { width: "100%", height: 40 }
      : { width: 200 },
  };
  const [SearchInput, searchValue] = useSearchInput(inputParams);
  const [assignLicensesModalOpen, setAssignLicensesModalOpen] = useState(false);
  const [selectedTelegramIds, setSelectedTelegramIds] = useState<number[]>([]);
  const [telegramIdToError, setTelegramIdToError] = useState<
    Record<number, AssignLicensesFailureType> | undefined
  >();

  let filteredSubGroupUsers = subGroupUsers
    .filter((user) => filterObjectsBySearch(searchValue, user, SEARCH_FIELDS))
    .map((user) => {
      return {
        ...user,
        key: user.telegram_user_id,
      };
    });

  let { component: filterComponent, filteredUsers } = useFilters(
    filteredSubGroupUsers,
    selectedTelegramIds,
    () => setAssignLicensesModalOpen(true),
    telegramIdToError ? () => setTelegramIdToError(undefined) : undefined,
    subGroupConfiguration?.allow_license
  );

  const errorTelegramIds = Object.keys(telegramIdToError || {});
  if (errorTelegramIds.length) {
    filteredUsers = filteredSubGroupUsers.filter((membership) =>
      errorTelegramIds.includes(membership.telegram_user_id.toString())
    );
  }

  // New look: an empty list says whether the search or the filters hid
  // every member, or that the group has none yet.
  const emptyReason = !subGroupUsers.length
    ? undefined
    : searchValue
    ? "search"
    : "filters";

  const licensesModal = (
    <LicensesModal
      open={assignLicensesModalOpen}
      closeModal={() => setAssignLicensesModalOpen(false)}
      subGroupID={subGroupID}
      selectedTelegramIds={selectedTelegramIds}
      setTelegramIdToError={setTelegramIdToError}
    />
  );

  // New look: the list is one panel - title, count and search in its
  // header, the filters in a toolbar row right above the table.
  if (isNewLookDesktop) {
    return (
      <>
        <TablePanel
          title={t("users.tabs.users")}
          count={filteredUsers.length || undefined}
          actions={SearchInput}
          flush={true}
        >
          <NewLookToolbar>
            <FilterPills>{filterComponent}</FilterPills>
          </NewLookToolbar>
          <PanelTable>
            <UsersTable
              subGroupID={subGroupID}
              filteredUsers={filteredUsers}
              selectedTelegramIds={selectedTelegramIds}
              setSelectedTelegramIds={setSelectedTelegramIds}
              telegramIdToError={telegramIdToError}
              setTelegramIdToError={setTelegramIdToError}
              isFetching={isFetching}
              emptyReason={emptyReason}
            />
          </PanelTable>
        </TablePanel>
        {licensesModal}
      </>
    );
  }

  // New look (phones): the filter chips stay on top; the members are one
  // panel of stacked rows with the search and the sort in its toolbar.
  if (isNewLook) {
    return (
      <>
        {filterComponent}
        <NewLookPhoneContent>
          {mobileActions}
          <TablePanel
            title={t("users.tabs.users")}
            count={filteredUsers.length || undefined}
            flush={true}
          >
            <UsersTable
              subGroupID={subGroupID}
              filteredUsers={filteredUsers}
              selectedTelegramIds={selectedTelegramIds}
              setSelectedTelegramIds={setSelectedTelegramIds}
              telegramIdToError={telegramIdToError}
              setTelegramIdToError={setTelegramIdToError}
              isFetching={isFetching}
              emptyReason={emptyReason}
              phoneToolbar={SearchInput}
            />
          </TablePanel>
        </NewLookPhoneContent>
        {licensesModal}
      </>
    );
  }

  return (
    <>
      {filterComponent}
      <div style={{ padding: isBottomNavigator ? 12 : 0 }}>
        {isBottomNavigator && mobileActions}
        <Flex $justifyContent="flex-end" style={{ margin: "16px 0 8px" }}>
          {SearchInput}
        </Flex>
        <UsersTable
          subGroupID={subGroupID}
          filteredUsers={filteredUsers}
          selectedTelegramIds={selectedTelegramIds}
          setSelectedTelegramIds={setSelectedTelegramIds}
          telegramIdToError={telegramIdToError}
          setTelegramIdToError={setTelegramIdToError}
          isFetching={isFetching}
        />
      </div>
      <LicensesModal
        open={assignLicensesModalOpen}
        closeModal={() => setAssignLicensesModalOpen(false)}
        subGroupID={subGroupID}
        selectedTelegramIds={selectedTelegramIds}
        setTelegramIdToError={setTelegramIdToError}
      />
    </>
  );
};

export default React.memo(Users);

const NewLookToolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px 4px;
`;
const NewLookPhoneContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 16px;
`;
